import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';

import Office from '../../models/Office/Office';
import OfficeCard from '../../components/office-card';
import { media, AnimateIn, Grid, GridItem, InternalLink, Container, Heading } from '../../ui-components';
import { orderOfficesByGeo } from '../../templates/about-us/our-offices';

const { dedupeLocalizedList } = require('../../utils/contentful-dedupe.jsx');

/**
 * Component that renders a rich-text content section block
 *
 * @component SectionOffices
 * 
 * @param {object} props
 * 
 * @prop {string} spaceId
 * 
 * @returns {JSX}
 */
const SectionOffices = props => {
  const { spaceId = process.env.CONTENTFUL_GLOBAL_SPACE_ID } = props;

  const data = useStaticQuery(graphql`
    query OfficeQuery {
      offices: allContentfulOffice(
        filter: { spaceId: { in: ["76f8cs5bg9si"] }}
        sort: { order: ASC }
      ) {
        edges {
          node {
            ...NoContentOffice
          }
        }
      }
    }
  `);

  let officeEdges = data.offices.edges;

  let offices = dedupeLocalizedList(officeEdges, spaceId).map(edge => {
    return new Office(edge.node);
  });

  if (spaceId === process.env.CONTENTFUL_AU_SPACE_ID) {
    offices = orderOfficesByGeo(offices, "AU");
  } else if (spaceId === process.env.CONTENTFUL_DE_SPACE_ID) {
    offices = orderOfficesByGeo(offices, "DE");
  } else if (spaceId === process.env.CONTENTFUL_IN_SPACE_ID) {
    offices = orderOfficesByGeo(offices, "IN");
  } else if (spaceId === process.env.CONTENTFUL_UK_SPACE_ID) {
    offices = orderOfficesByGeo(offices, "UK");
  } else if (spaceId === process.env.CONTENTFUL_US_SPACE_ID) {
    // Nothing special for US upon request
  }

  
  /**
   * Renders the grid items for a set of locations
   *
   * @function renderLocationList
   * 
   * @returns {JSX}
   */
  const renderLocationList = () => {
    return offices.map(location => (
      <OfficeGridItem col={4} colxl={6} collg={6} colmd={12} key={location.locationName}>
        <OfficeCard office={location} />
      </OfficeGridItem>
    ));
  };

  const renderHeading = () => {
    const headingText = 'Credera offices';

    return (
      <>
        <Heading level={2}>{headingText}</Heading>
        <OfficesTopLinkWrapper>
          <InternalLink to="/about-us/our-offices" withArrow>
            VIEW ALL OFFICES
          </InternalLink>
        </OfficesTopLinkWrapper>
      </>
    );
  };

  return (
    <AnimateIn onScroll>
      <OfficesContainer sectionContainer>
        <OfficesTitleSection>{renderHeading()}</OfficesTitleSection>
        <Grid>{renderLocationList()}</Grid>
      </OfficesContainer>
    </AnimateIn>
  );
};

const OfficesContainer = styled(Container)`
  padding-top: 0;
`;

const OfficeGridItem = styled(GridItem)`
  justify-content: center;
`;

const OfficesTitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: normal;

  @media screen and (max-width: ${media.mediumBottom}) {
    align-self: center;
  }
`;

const OfficesTopLinkWrapper = styled.div`
  align-self: center;
  padding-top: 8px;
  
  @media screen and (max-width: ${media.mediumBottom}) {
    display: none;
  }
`;

export default SectionOffices;
